import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import { Box } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useNowEveryMinute } from "../../../../hooks/useNowEveryMinute";
import { useRequestOrigins } from "../../../../hooks/useRequestOrigins";
import { OPEN_SHIFTS } from "../../../../theme/tokens";
import type { OpenShiftRequest, ReplaceShiftRequest } from "../../../../utils/models";
import { useDepartmentReuqestAgentIds } from "../../../RequestData/hooks/useAgentHistory";
import { JumpingDots } from "../../../UIToolkit/Animations/JumpingDots";
import { EmptyState } from "../../../UIToolkit/molecules/EmptyState";
import { groupRequestsByStaffingDay } from "../gapsRequests";
import { GapsSheetKind, openGapsSheet } from "../useGapsSheet";
import { GapsRequestCard } from "./GapsRequestCard";
import type { GapsApplicant } from "./useGapsApplicants";
import { GapsOpenShiftsLoad, useGapsOpenShiftsLoad } from "./useGapsOpenShiftsLoad";

type Props = {
	/** The day's posted requests, already narrowed by the saved scope (the Posted tab's own list). */
	requests: OpenShiftRequest[];
};

/**
 * The home's Posted tab: the day's posted requests, drawn with Open Shifts' own card, so a post reads — and acts — the
 * same on both screens (the one surface's tap opens the posted-shift sheet, the other's Approve the confirm). Replaces the
 * classic `PostedShiftsList` here, whose card taps left for the classic request page and whose icon row was built for a
 * mouse.
 *
 * Earliest start first, the order Open Shifts lists a day in; the 48h band's edge marks an urgent claim here too.
 */
export const GapsPostedList: FC<Props> = ({ requests }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { data: requestsAgentIds } = useDepartmentReuqestAgentIds();
	const load = useGapsOpenShiftsLoad();
	const departmentId = chosenDepartment.department_id;
	const origins = useRequestOrigins(departmentId, requests);

	// One staffing day's requests, so the grouping yields a single group; it is used for its order and its 48h flags —
	// against a "now" that moves on each minute, so the flags stay true while the tab sits open.
	const now = useNowEveryMinute();
	const entries = useMemo(() => {
		const rule = { dayStart: chosenDepartment.dayStart, scheduled_shift_delta_minutes: chosenDepartment.scheduled_shift_delta_minutes };
		return groupRequestsByStaffingDay(requests, now, rule).flatMap((day) => day.entries);
	}, [requests, now, chosenDepartment.dayStart, chosenDepartment.scheduled_shift_delta_minutes]);

	const handleOpenRequest = useCallback(
		(request: OpenShiftRequest | ReplaceShiftRequest): void => {
			track("gaps_posted_row_opened", { department_id: departmentId, request_id: request.id });
			openGapsSheet({ kind: GapsSheetKind.PostedActions, requestId: request.id });
		},
		[departmentId, track],
	);

	const handleApprove = useCallback(
		(request: OpenShiftRequest | ReplaceShiftRequest, applicant: GapsApplicant): void => {
			track("gaps_posted_approve_clicked", { department_id: departmentId, request_id: request.id, nurse_id: applicant.nurseId });
			openGapsSheet({ kind: GapsSheetKind.ApproveConfirm, requestId: request.id, nurseId: applicant.nurseId });
		},
		[departmentId, track],
	);

	// A failed load is not "nothing posted" (the same rule as Open Shifts); a first load still settling shows nothing yet.
	if (load === GapsOpenShiftsLoad.Failed) {
		return (
			<EmptyState
				dataTestId="gaps-posted-load-failed"
				icon={<ErrorOutlineOutlinedIcon />}
				iconColor="error.main"
				title={t("gaps.requests.loadFailed")}
				hint={t("gaps.requests.loadFailedHint")}
				sx={{ py: 5 }}
			/>
		);
	}
	if (load === GapsOpenShiftsLoad.Loading) {
		return (
			<Box data-testid="gaps-posted-loading" sx={{ display: "flex", justifyContent: "center", py: 5 }}>
				<JumpingDots />
			</Box>
		);
	}
	if (entries.length === 0) {
		return <EmptyState dataTestId="gaps-posted-empty" icon={<PendingActionsOutlinedIcon />} title={t("gaps.requests.emptyDay")} sx={{ py: 5 }} />;
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: OPEN_SHIFTS.CARD_GAP }}>
			{entries.map((entry) => (
				<GapsRequestCard
					key={entry.request.id}
					request={entry.request}
					isUrgentClaim={entry.isUrgentClaim}
					isExpandedByList={entry.isUrgentClaim}
					// DEFAULT: while the call-off agent's list loads (or the flag is off) no robo call is known to be out.
					isRoboCallRunning={requestsAgentIds?.includes(entry.request.id) === true}
					origins={origins.byRequestId.get(entry.request.id) ?? null}
					now={now}
					onOpenRequest={handleOpenRequest}
					onApprove={handleApprove}
				/>
			))}
		</Box>
	);
};
